import SideTool from "@/global/components/side-tool";
import './index.less';
import { Input, Modal, Skeleton, Tree, TreeDataNode } from "antd";
import Icon, { DownOutlined } from '@ant-design/icons';
import { ReactComponent as RefreshSvg } from './icons/refresh.svg';
import { ReactComponent as AnemoclinographSvg } from './icons/anemoclinograph.svg';
import { ReactComponent as WaterSvg } from './icons/water-level-gauge.svg';
import { ReactComponent as CameraSvg } from './icons/camera.svg';
import { ReactComponent as CameraGreySvg } from './icons/camera-grey.svg';
import { ReactComponent as SerialSvg } from './icons/serial.svg';
import { ReactComponent as DvrSvg } from './icons/dvr.svg';
import { ReactComponent as LinkSvg } from './icons/link-blue.svg';
import { ReactComponent as StartSvg } from './icons/start.svg';
import { ReactComponent as StopSvg } from './icons/stop.svg';
import { ReactComponent as PassSvg } from './icons/pass.svg';
import { ReactComponent as UnpassSvg } from './icons/unpass.svg';
import { ReactComponent as WariningGreySvg } from './icons/warining-grey.svg';
import { ReactComponent as CheckedSvg } from './icons/checked.svg';
import { ReactComponent as CheckSvg } from './icons/check.svg';
import { ReactComponent as InfoSvg } from './icons/info.svg';
import { ReactComponent as EditSvg } from './icons/edit.svg';
import { ReactComponent as DeleteSvg } from './icons/delete.svg';
import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { getDeviceList } from "@/api/project-device/project-device";
import TnMessage from "@/global/components/message";
import classNames from "classnames";
import { DEVICE_TYPE } from "@/enums/device-type";
import { DATA_BOX_TYPE } from "@/enums/data-box-type";
import { SENSOR_TYPE } from "@/enums/sensor-type";
import { WORK_STATUS } from "@/enums/work-status";
import React from "react";
import { CommonTabbarContext } from "@/App";
import { CommonTabbarState } from "@/global/components/common-tabbar";
import { ContextMenuFC } from "tncet-common";
import { graphicStore } from "@/global/store/graphic-store";
import TnModal from "@/global/components/base-modal";
import { addDataBoxCollectionHistory, deleteDataBox, deleteDataBoxes, updateDataBox } from "@/api/device/data-box";
import { ModelBase } from "pytha";
import { addSensorCollectionHistory, deleteSensor, deleteSensors, updateSensor } from "@/api/device/sensor";
import { MonixEraseCommand } from "@/passets/pytha-engine/commands/MonixEraseCommand";
import axios from "axios";
import { OrthographicCamera } from "three";
import { TAB_TYPE } from "@/enums/tab-type";
import TnInput from "@/global/components/input/tn-input";
import { StdResponse } from "@/global/config/request";
import { emitter } from "@/global/listeners";
import { Emit_Event } from "@/enums/emit-event";

interface DeviceTreeNode {
    id?: number
    code?: string
    level?: number
    workStatus?: number
    deviceType?: string
    type?: number
    primitiveUuid?: string
    start?: boolean
    children?: DeviceTreeNode[]
}

export default function DeviceListPanel() {
    const { projectId } = useParams();
    const location = useLocation(); // 路由
    const [loading, setLoading] = useState<boolean>(false); // 页面的loading
    const [spinning, setSpinning] = useState<boolean>(false); // 刷新按钮的loading
    const [treeData, setTreeData] = useState<DeviceTreeNode[]>([]); // 设备表
    const [cmOpen, setCmOpen] = useState<boolean>(false); // 是否展开右键菜单
    const [top, setTop] = useState<number>(); // 右键菜单top
    const [left, setLeft] = useState<number>(); // 右键菜单left
    const [activeNode, setActiveNode] = useState<DeviceTreeNode>(); // 右键选中的节点
    const [multiple, setMultiple] = useState<boolean>(false); // 是否开启多选
    const [selectedNodes, setSelectedNodes] = useState<DeviceTreeNode[]>([]); // 多选选中的节点
    const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null); // 区别单击双击的ref
    const commonTabbarContext = useContext<CommonTabbarState>(CommonTabbarContext); // tab上下文
    const [editing, setEditing] = useState<boolean>(false); // 是否处于编辑状态
    const [editNode, setEditNode] = useState<DeviceTreeNode>(null); // 正在编辑的节点

    // 初始化
    useEffect(() => {
        generateData();
    }, [projectId])

    // 监听多选的变化
    useEffect(() => {
        if (!multiple) {
            setSelectedNodes([]);
        }
    }, [multiple])

    // 监听路由，高亮左侧列表
    useEffect(() => {
        let pathName = location.pathname;

        const match = pathName.match(/\/bim\/project\/\d+\/device\/(\d+)\/(\w+)/);
        if (match) {
            const id = match[1];
            const type = match[2];
            let node = findTreeNode(treeData, type, +id);
            setActiveNode(node)
        } else {
            setActiveNode(null);
        }
    }, [location.pathname])

    // 监听采集信号和停止采集信号
    useEffect(() => {
        emitter.on(Emit_Event.START_COLLECTION, startCollectionClick)
        emitter.on(Emit_Event.STOP_COLLECTION, stopCollectionClick)
        return (() => {
            emitter.off(Emit_Event.START_COLLECTION, startCollectionClick)
            emitter.off(Emit_Event.STOP_COLLECTION, stopCollectionClick)
        })
    }, [selectedNodes])

    // 批量启动采集
    const startCollectionClick = () => {
        if (!(selectedNodes?.length > 0)) return
        let startPromise = []
        for (let i = 0; i < selectedNodes.length; i++) {
            let node = selectedNodes[i]
            if (node.deviceType == DEVICE_TYPE.DATA_BOX) {
                if (node.start) continue;
                startPromise.push(updateDataBox(node.id, { start: true }))
                startPromise.push(addDataBoxCollectionHistory(Number(projectId), node.id, { personName: "用户", start: true }))
            } else if (node.deviceType == DEVICE_TYPE.SENSOR) {
                if (node.start) continue;
                startPromise.push(updateSensor(node.id, { start: true }))
                startPromise.push(addSensorCollectionHistory(Number(projectId), node.id, { personName: "用户", start: true }))
            }
        }
        axios.all(startPromise).then(res => {
            generateData()
        }).catch((e) => {
            TnMessage.error("启动失败！")
        }).finally(() => {
            setMultiple(false)
        })
    }

    // 批量停止采集
    const stopCollectionClick = () => {
        if (!(selectedNodes?.length > 0)) return
        let startPromise = []
        for (let i = 0; i < selectedNodes.length; i++) {
            let node = selectedNodes[i]
            if (node.deviceType == DEVICE_TYPE.DATA_BOX) {
                if (!node.start) continue;
                startPromise.push(updateDataBox(node.id, { start: false }))
                startPromise.push(addDataBoxCollectionHistory(Number(projectId), node.id, { personName: "用户", start: false }))
            } else if (node.deviceType == DEVICE_TYPE.SENSOR) {
                if (!node.start) continue;
                startPromise.push(updateSensor(node.id, { start: false }))
                startPromise.push(addSensorCollectionHistory(Number(projectId), node.id, { personName: "用户", start: false }))
            }
        }
        axios.all(startPromise).then(res => {
            generateData()
        }).catch((e) => {
            TnMessage.error("暂停失败！")
        }).finally(() => {
            setMultiple(false)
        })
    }

    // 获取树节点
    const findTreeNode = (tree: DeviceTreeNode[], deviceType: string, id: number) => {
        for (let node of tree) {
            // 判断当前节点是否匹配
            if (node?.deviceType === deviceType && node?.id === id) {
                return node;
            }
            // 如果有子节点，递归查找子节点
            if (node?.children && node?.children?.length > 0) {
                const found = findTreeNode(node?.children, deviceType, id);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    }

    // 点击刷新按钮
    const refresh = () => {
        setSpinning(true);
        generateData();
    }

    // 获取列表
    const generateData = () => {
        if (!projectId || projectId === 'undefined') return;
        setLoading(true);
        setTreeData([]);
        getDeviceList(+projectId).then(res => {
            let data: DeviceTreeNode[] = res.data || [];
            setTreeData([...data]);
        }).catch(err => {
            TnMessage.warning('获取设备表失败');
        }).finally(() => {
            setLoading(false);
            setSpinning(false);
        })
    }

    // 点击通用工具栏的删除按钮或右键删除
    const onDeleteClick = (multiple: boolean, activeNode: DeviceTreeNode, selectedNodes: DeviceTreeNode[]) => {
        if ((!multiple && !activeNode) || (multiple && selectedNodes?.length === 0)) {
            TnMessage.info("未选中设备");
            return;
        }
        // 删除
        TnModal().deleteModal({
            onOk() {
                if (!multiple) {
                    // 单选
                    if (activeNode.deviceType === DEVICE_TYPE.DATA_BOX) {
                        setLoading(true);
                        deleteDataBox(activeNode.id).then(res => {
                            if (res.status === 200) {
                                generateData();
                                if (!graphicStore?.extraContext?.getCurrentViewEditor()) return;
                                if (!!activeNode?.primitiveUuid) {
                                    const editor = graphicStore.extraContext.getCurrentViewEditor();
                                    // 存在图元，删除图元
                                    let entity = editor?.entityDict[activeNode?.primitiveUuid];
                                    if (!entity) return;
                                    new MonixEraseCommand([entity]).execute();
                                    editor.listeners.signals.needRender.dispatch('redraw');
                                }
                            }
                        }).catch(err => {
                            TnMessage.error("删除失败");
                        }).finally(() => {
                            setLoading(false);
                            Modal.destroyAll();
                            setCmOpen(false);
                        })
                    } else if (activeNode.deviceType === DEVICE_TYPE.SENSOR) {
                        setLoading(true);
                        deleteSensor(activeNode.id).then(res => {
                            if (res.status === 200) {
                                if (!graphicStore?.extraContext?.getCurrentViewEditor()) return;
                                if (!activeNode?.primitiveUuid) return;
                                const editor = graphicStore.extraContext.getCurrentViewEditor();
                                // 存在图元，删除图元
                                let entity = editor?.entityDict[activeNode?.primitiveUuid];
                                if (!entity) return;
                                new MonixEraseCommand([entity]).execute();
                                editor.listeners.signals.needRender.dispatch('redraw');
                                generateData();
                            }
                        }).catch(err => {
                            TnMessage.error("删除失败");
                        }).finally(() => {
                            setLoading(false);
                            Modal.destroyAll();
                            setCmOpen(false);
                        })
                    }
                } else {
                    // 多选
                    let { dataBoxIds, sensorIds, primitiveUuids } = selectedNodes?.reduce((pre, cur) => {
                        if (cur.deviceType === DEVICE_TYPE.DATA_BOX) {
                            pre.dataBoxIds.push(cur.id);
                        } else if (cur.deviceType === DEVICE_TYPE.SENSOR) {
                            pre.sensorIds.push(cur.id);
                        }
                        if (!!cur?.primitiveUuid) {
                            pre.primitiveUuids.push(cur.primitiveUuid);
                        }
                        return pre;
                    }, { dataBoxIds: [], sensorIds: [], primitiveUuids: [] });
                    let promiseList = [];
                    if (dataBoxIds?.length > 0) {
                        promiseList.push(() => deleteDataBoxes(dataBoxIds));
                    }
                    if (sensorIds?.length > 0) {
                        promiseList.push(() => deleteSensors(sensorIds));
                    }
                    if (promiseList?.length > 0) {
                        setLoading(true);
                        axios.all(promiseList?.map(fn => fn())).then(res => {
                            generateData();
                            if (!graphicStore?.extraContext?.getCurrentViewEditor()) return;
                            if (primitiveUuids?.length === 0) return;

                            const editor = graphicStore.extraContext.getCurrentViewEditor();
                            // 取消选中
                            let selected = editor.selectControl.getSelectedEntityList();
                            let devices = selected?.filter(e => primitiveUuids?.some(p => p === e.uuid));
                            devices.forEach(d => {
                                d.isSelected = false;
                                delete editor.selectControl.getSelectedEntities()[d?.uuid];
                            })
                            editor.listeners.signals.onSelectChanged.dispatch(devices);

                            // 存在图元，删除图元
                            const deletePris = editor.getEntities()?.filter(item => primitiveUuids?.some(p => p === item.uuid))
                            new MonixEraseCommand(deletePris).execute();
                            editor.listeners.signals.needRender.dispatch('redraw');
                            TnMessage.success("删除成功");

                        }).catch(err => {
                            TnMessage.error("删除失败");
                        }).finally(() => {
                            Modal.destroyAll();
                        })
                    }

                }
            },
            onCancel() {
                Modal.destroyAll();
            },
            closable: true
        })
    }

    // 获取设备类型的icon
    const getDeviceTypeIcon = (item: DeviceTreeNode) => {
        let deviceType = item.deviceType;
        let type = item.type;
        if (deviceType === DEVICE_TYPE.DATA_BOX) {
            if (type === DATA_BOX_TYPE.SERIAL) {
                return <SerialSvg />
            } else if (type === DATA_BOX_TYPE.DVR) {
                return <DvrSvg />
            }
        } else if (deviceType === DEVICE_TYPE.SENSOR) {
            if (type === SENSOR_TYPE.ANEMOCLINOGRAPH) {
                return <AnemoclinographSvg />
            } else if (type === SENSOR_TYPE.FLUVIOGRAPH) {
                return <WaterSvg />
            } else if (type === SENSOR_TYPE.CAMERA) {
                return <CameraSvg />
            }
        }
    }

    // 获取设备状态的icon
    const getDeviceWorkStatusIcon = (item: DeviceTreeNode) => {
        let start = item?.start;
        if (start) {
            return <StartSvg />
        } else {
            return <StopSvg />
        }
        // let workStatus = item.workStatus;
        // if (workStatus === WORK_STATUS.MODEL_ASSEMBLE) {
        //     return <WariningGreySvg />
        // } else if (workStatus === WORK_STATUS.PLANT_CONNECTED) {
        //     return <LinkSvg />
        // } else if (workStatus === WORK_STATUS.TEST_PASSED) {
        //     return <PassSvg />
        // } else if (workStatus === WORK_STATUS.TEST_FAILED) {
        //     return <UnpassSvg />
        // } else if (workStatus === WORK_STATUS.DURING_COLLECTION) {
        //     return <StartSvg />
        // } else if (workStatus === WORK_STATUS.PAUSE_COLLECTION) {
        //     return <StopSvg />
        // }
        // return <></>
    }

    // 右键设备
    const onRowContextMenu = (e, item: DeviceTreeNode) => {
        setCmOpen(true);
        setLeft(e.clientX);
        setTop(e.clientY);
        setActiveNode(item);
    }

    // 多选状态下点击选择框
    const checkBoxClick = (e, item: DeviceTreeNode) => {
        e.stopPropagation();
        e.preventDefault();
        let newNodes = [...selectedNodes];
        let idx = selectedNodes?.findIndex(i => i.id === item.id && i.deviceType === item.deviceType);
        if (idx === -1) {
            newNodes.push(item);
        } else {
            newNodes.splice(idx, 1);
        }
        setSelectedNodes(newNodes);
        if (!graphicStore?.extraContext?.getCurrentViewEditor()) return;
        if (!item?.primitiveUuid) return;
        // 当前处于绘图区，并且选中设备有图元
        const editor = graphicStore.extraContext.getCurrentViewEditor();
        if (idx === -1) {
            // 选中
            if (editor.entityDict[item?.primitiveUuid] && editor.entityDict[item?.primitiveUuid].isSelected) return;
            editor.entityDict[item?.primitiveUuid].isSelected = true;
            editor.selectControl.forceEntitiesSelected([editor.entityDict[item?.primitiveUuid]])
            showEntity(editor.selectControl.getSelectedEntityList());
            editor.listeners.signals.needRender.dispatch('redraw');
        } else {
            // 取消选中
            let selected = editor.selectControl.getSelectedEntityList();
            let device = selected?.find(e => e.uuid === item?.primitiveUuid);
            if (!device) return;
            device.isSelected = false;
            editor.entityDict[item?.primitiveUuid].isSelected = false;
            delete editor.selectControl.getSelectedEntities()[item?.primitiveUuid];
            editor.listeners.signals.onSelectChanged.dispatch([device]);
            showEntity(editor.selectControl.getSelectedEntityList());
            editor.listeners.signals.needRender.dispatch('redraw');
        }
    }

    // 单击树节点
    const theeNodeClick = (node: DeviceTreeNode) => {
        if (clickTimeoutRef.current) {
            clearTimeout(clickTimeoutRef.current);
        }
        clickTimeoutRef.current = setTimeout(() => {
            setActiveNode(node);
            if (!graphicStore?.extraContext?.getCurrentViewEditor()) return;
            if (!node?.primitiveUuid) return;
            const editor = graphicStore.extraContext.getCurrentViewEditor();
            let entity = editor.entityDict[node?.primitiveUuid];
            showEntity([entity]);
        }, 300);
    }

    // 双击树节点
    const theeNodeDoubleClick = (node: DeviceTreeNode) => {
        if (clickTimeoutRef.current) {
            clearTimeout(clickTimeoutRef.current);
            clickTimeoutRef.current = null;
        }
        setActiveNode(node);
        showDeviceDetail(node);
    }

    // 跳转至设备详情
    const showDeviceDetail = (node: DeviceTreeNode) => {
        if (!node) return;
        setCmOpen(false);
        commonTabbarContext.navigate(`${TAB_TYPE.DEVICE}/${node.id}/${node.deviceType}`)
    }

    // 编辑树节点
    const editTheeNode = (node: DeviceTreeNode) => {
        if (!node) {
            TnMessage.warning("未选中设备");
        }
        setEditing(true);
        setEditNode(node);
        setCmOpen(false);
    }

    // 编辑完成
    const editTheeNodeFinish = (code: string) => {
        if (!editNode) {
            setEditing(false);
            setActiveNode(null);
            return;
        }
        let promise: Promise<StdResponse<any>>;
        if (editNode.deviceType === DEVICE_TYPE.DATA_BOX) {
            promise = updateDataBox(editNode.id, { code: code })
        } else if (editNode.deviceType === DEVICE_TYPE.SENSOR) {
            promise = updateSensor(editNode.id, { code: code });
        }
        if (!promise) {
            setEditing(false);
            setEditNode(null);
            setActiveNode(null);
            return;
        }
        setLoading(true);
        promise.then(res => {
            if (res.status === 200) {
                TnMessage.success("编辑成功");
                generateData();
                setEditing(false);
                setEditNode(null);
                setActiveNode(null);
            }
        }).catch(err => {
            TnMessage.error("编辑失败");
        })

    }

    /**
     * 渲染设备树
     * 当树节点满足：1、不是第0层 2、有兄弟节点 3、该节点不是兄弟节点中的最后一个节点 => 渲染连接线
     * @param nodes 树节点
     * @param level 树层级
     * @param renderLineList 连接线渲染列表 
     */
    const renderTreeNodes = (nodes: DeviceTreeNode[], level: number, renderLineList: boolean[]) => {
        return nodes?.map((item: DeviceTreeNode, key: number) => {
            const hasChildren = item?.children?.length > 0;
            const hasBrotherFlag = nodes?.length !== 1;
            const notLastBrother = key !== nodes?.length - 1;
            let childRenderLineList = [...renderLineList];

            if (level !== 0) {
                childRenderLineList.push((hasBrotherFlag && notLastBrother));
            }
            return (
                <React.Fragment key={`${item.deviceType}-${item.id}`}>
                    <div className={classNames({
                        "device-item": true,
                        "device-item-active": (!multiple && activeNode?.id === item.id && activeNode?.deviceType === item.deviceType)
                            || (multiple && selectedNodes?.some(i => i.id === item.id && i.deviceType === item.deviceType))
                    })}
                        style={{ paddingLeft: level > 0 ? 48 : 40, }}
                        onContextMenu={(e) => { e.preventDefault(); onRowContextMenu(e, item) }}
                        onClick={() => theeNodeClick(item)}
                        onDoubleClick={() => theeNodeDoubleClick(item)}
                    >
                        {multiple && <div className="check-box" onClick={(e) => checkBoxClick(e, item)}>
                            {!selectedNodes?.some(n => n.id === item.id && n.deviceType === item.deviceType) && <CheckSvg />}
                            {selectedNodes?.some(n => n.id === item.id && n.deviceType === item.deviceType) && <CheckedSvg />}
                        </div>}
                        {/* 显示从父节点到当前节点的层级连线 */}
                        {renderLineList?.map((bool, index) => (
                            <div className="level-link-line" key={index} style={{ backgroundColor: bool ? '#212121' : 'transparent' }} />
                        ))}

                        {/* 显示从当前节点向下延伸的垂直连线 */}
                        {level > 0 && (<>
                            <div className="h-line" style={{ height: key === nodes?.length - 1 ? '50%' : '100%' }} />
                            <div className="v-line" />
                        </>)}
                        <div className="device-icon">{getDeviceTypeIcon(item)}</div>
                        <div className="device-name">
                            {!(editing && editNode?.id === item.id && editNode?.deviceType === item.deviceType) && `${item?.code || ''}`}
                            {(editing && editNode?.id === item.id && editNode?.deviceType === item.deviceType) && <TnInput
                                autoFocus
                                defaultValue={item?.code}
                                onBlur={(str) => editTheeNodeFinish(str)}
                                onPressEnter={(str) => editTheeNodeFinish(str)}
                            />}
                        </div>
                        <div className="status-icon">{getDeviceWorkStatusIcon(item)}</div>
                    </div>
                    {/* 递归渲染子节点 */}
                    {hasChildren && renderTreeNodes(item.children, level + 1, childRenderLineList)}
                </React.Fragment >
            );
        }) || [];
    };

    // 单击设备，视角锁定到被点击的设备
    const showEntity = (models: ModelBase[]) => {
        if (!graphicStore.extraContext.getCurrentViewEditor()) return;
        if (!models || models.length == 0) {
            return;
        }

        let x1 = +Infinity
        let y1 = +Infinity
        let x2 = -Infinity
        let y2 = -Infinity

        models.forEach(model => {
            let box = model.getBoundingBox();
            box.min.project(graphicStore.extraContext.getCurrentViewEditor().camera);
            box.max.project(graphicStore.extraContext.getCurrentViewEditor().camera);
            x1 = Math.min(box.min.x, x1);
            y1 = Math.min(box.min.y, y1);
            x2 = Math.max(box.max.x, x2);
            y2 = Math.max(box.max.y, y2);
        })

        if (x1 == null || y1 == null || x2 == null || y2 == null) {
            return
        }

        let scale1 = Math.abs(y1 - y2) / 2;

        let scale2 = Math.abs(x1 - x2) / 2;
        // max
        let scale = Math.max(scale1, scale2) * 4.3;
        let x = (x1 + x2) / 2;
        let y = (y1 + y2) / 2;
        let camera = graphicStore.extraContext.getCurrentViewEditor().camera;
        let target = graphicStore.context.ucsContext.unprojectCursorPoint(x, y, camera);

        let lastTarget = graphicStore.extraContext.getCurrentViewEditor().viewControl.target.clone();
        let offset = camera.position.clone().sub(lastTarget);
        const position = camera.position;
        graphicStore.extraContext.getCurrentViewEditor().viewControl.target.copy(target);
        position.copy(target).add(offset);
        graphicStore.extraContext.getCurrentViewEditor().camera.lookAt(target);
        // scale 框和当前边界的比例
        graphicStore.context.renderContext.cameraEdge *= scale;
        if (camera.type === "OrthographicCamera") {
            let orthoCamera = camera as OrthographicCamera;
            orthoCamera.left = orthoCamera.left * scale;
            orthoCamera.right = orthoCamera.right * scale;
            orthoCamera.top = orthoCamera.top * scale;
            orthoCamera.bottom = orthoCamera.bottom * scale;
            orthoCamera.updateProjectionMatrix();
        }


        graphicStore.extraContext.listeners.signals.onZoomChanged.dispatch(1 / scale);
    }


    return (
        <div className="device-list-container">
            <SideTool
                onSelectClick={() => setMultiple(true)}
                onUnSelectClick={() => setMultiple(false)}
                onEditClick={() => editTheeNode(activeNode)}
                onDeleteClick={() => onDeleteClick(multiple, activeNode, selectedNodes)}
            />
            <div className="device-list-panel">
                <div className="refresh-btn" onClick={refresh}>
                    <Icon component={RefreshSvg} className="icon" spin={spinning} />刷新
                </div>
            </div>
            <Skeleton loading={loading}>
                <div className="device-list-body">
                    {treeData?.length !== 0 && renderTreeNodes(treeData, 0, [])}
                </div>
            </Skeleton>

            <ContextMenuFC
                visible={cmOpen}
                top={top}
                left={left}
                width={96}
                wrapperClassname='device-list-right-menu'
                onCancel={() => { setCmOpen(false); setActiveNode(null); }}
            >
                <div className="menu-item" onClick={() => showDeviceDetail(activeNode)}><InfoSvg />设备详情</div>
                <div className="menu-item" onClick={() => editTheeNode(activeNode)}><EditSvg />编辑</div>
                <div className="menu-item" onClick={() => onDeleteClick(multiple, activeNode, [])}><DeleteSvg />删除</div>
            </ContextMenuFC>
        </div>
    )
}